import React, { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAuth } from '../../../shared/hooks/useAuth.js'
import { API_BASE_URL, APP_URL } from '../../../shared/utils/config'
import './Projects.css'

function ProjectsTest() {
  const navigate = useNavigate()
  const { user, signOut, getAuthHeaders } = useAuth()
  const [projects, setProjects] = useState([])
  const [showNewProjectModal, setShowNewProjectModal] = useState(false)
  const [newProjectName, setNewProjectName] = useState('')
  const [newProjectDescription, setNewProjectDescription] = useState('')
  const [searchTerm, setSearchTerm] = useState('')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)

  // API_BASE_URL은 config에서 자동으로 가져옴 (개발/운영 자동 선택)

  // 백엔드에서 프로젝트 목록 불러오기
  useEffect(() => {
    if (user) {
      loadProjectsFromBackend()
    }
  }, [user])

  // 백엔드 API로 프로젝트 목록 가져오기 (기존 API와 새 API 둘 다 시도)
  const loadProjectsFromBackend = async () => {
    setLoading(true)
    setError(null)
    
    try {
      // 먼저 새로운 video-projects API 시도
      let response = await fetch(`${API_BASE_URL}/api/video-projects/list`, {
        headers: getAuthHeaders()
      })
      
      // 새 API가 없으면 기존 API 사용
      if (!response.ok && response.status === 404) {
        console.log('video-projects API 없음, 기존 projects API 사용')
        response = await fetch(`${API_BASE_URL}/api/projects/list`, {
          headers: getAuthHeaders()
        })
      }
      
      if (response.ok) {
        const data = await response.json()
        console.log('백엔드 프로젝트 목록:', data)
        setProjects(data.projects || [])
      } else {
        const errorData = await response.json()
        setError(errorData.detail || '프로젝트 목록을 불러올 수 없습니다')
      }
    } catch (error) {
      console.error('프로젝트 목록 조회 실패:', error)
      setError('서버 연결에 실패했습니다')
    } finally {
      setLoading(false)
    }
  }

  // OAuth 에러 처리
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search)
    const error = urlParams.get('error')
    const errorDescription = urlParams.get('description') || urlParams.get('error_description')
    
    if (error) {
      console.error('로그인 오류:', { error, description: errorDescription })
      let message = '로그인에 실패했습니다.'
      if (error === 'server_error') {
        message = '서버 오류가 발생했습니다. 잠시 후 다시 시도해주세요.'
      } else if (error === 'access_denied') {
        message = '로그인이 취소되었습니다.'
      }
      alert(message)
      window.history.replaceState({}, document.title, '/test/projects-api')
    }
  }, [])

  const handleKakaoLogin = async () => {
    console.log('카카오 로그인 시작...')
    try {
      const redirectUrl = `${APP_URL}/auth/callback`
      
      console.log('API 호출:', `${API_BASE_URL}/api/auth/supabase/kakao/login`)
      console.log('Redirect URL:', redirectUrl)
      
      const response = await fetch(`${API_BASE_URL}/api/auth/supabase/kakao/login?redirect_to=${encodeURIComponent(redirectUrl)}`)
      
      console.log('Response status:', response.status)
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`)
      }
      
      const data = await response.json()
      console.log('Response data:', data)
      
      if (data.auth_url) {
        console.log('Redirecting to:', data.auth_url)
        window.location.href = data.auth_url
      } else {
        console.error('auth_url이 없습니다:', data)
        alert('로그인 URL을 가져올 수 없습니다.')
      }
    } catch (error) {
      console.error('카카오 로그인 실패 상세:', error)
      alert(`로그인 중 오류가 발생했습니다: ${error.message}`)
    }
  }

  // 백엔드 API로 새 프로젝트 생성 (video-projects 엔드포인트 사용)
  const createNewProject = async () => {
    if (!user) {
      alert('프로젝트를 만들려면 먼저 로그인해주세요.')
      return
    }
    
    if (!newProjectName.trim()) {
      alert('프로젝트 이름을 입력해주세요')
      return
    }

    setLoading(true)
    setError(null)

    try {
      // 새로운 video-projects API 사용
      const response = await fetch(`${API_BASE_URL}/api/video-projects/create`, {
        method: 'POST',
        headers: {
          ...getAuthHeaders(),
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          name: newProjectName,
          description: newProjectDescription || null,
          sections: [],  // 비디오 편집기용 sections 배열
          settings: {
            createdAt: new Date().toISOString()
          }
        })
      })

      if (response.ok) {
        const data = await response.json()
        console.log('프로젝트 생성 성공:', data)
        
        // 프로젝트 목록 새로고침
        await loadProjectsFromBackend()
        
        setNewProjectName('')
        setNewProjectDescription('')
        setShowNewProjectModal(false)
        
        // 새 프로젝트로 이동
        if (data.project && data.project.id) {
          navigate(`/editor/${data.project.id}`)
        }
      } else {
        const errorData = await response.json()
        alert(errorData.detail || '프로젝트 생성에 실패했습니다')
      }
    } catch (error) {
      console.error('프로젝트 생성 실패:', error)
      alert('프로젝트 생성 중 오류가 발생했습니다')
    } finally {
      setLoading(false)
    }
  }

  // 프로젝트 열기
  const openProject = (projectId) => {
    navigate(`/editor/${projectId}`)
  }

  // 프로젝트 섹션 저장 (새로운 API)
  const saveProjectSections = async (projectId, sections) => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/video-projects/save-sections`, {
        method: 'POST',
        headers: {
          ...getAuthHeaders(),
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          project_id: projectId,
          sections: sections
        })
      })

      if (response.ok) {
        const data = await response.json()
        console.log('섹션 저장 성공:', data)
        return data
      } else {
        throw new Error('섹션 저장 실패')
      }
    } catch (error) {
      console.error('섹션 저장 오류:', error)
      throw error
    }
  }

  // 프로젝트 상세 정보 가져오기 (섹션 포함)
  const getProjectDetail = async (projectId) => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/video-projects/${projectId}`, {
        headers: getAuthHeaders()
      })

      if (response.ok) {
        const data = await response.json()
        console.log('프로젝트 상세 정보:', data)
        return data
      } else {
        throw new Error('프로젝트 정보 가져오기 실패')
      }
    } catch (error) {
      console.error('프로젝트 정보 가져오기 오류:', error)
      throw error
    }
  }

  // 백엔드 API로 프로젝트 삭제
  const deleteProject = async (e, projectId) => {
    e.stopPropagation()
    const project = projects.find(p => p.id === projectId)
    
    if (window.confirm(`"${project.name}" 프로젝트를 삭제하시겠습니까?\n이 작업은 되돌릴 수 없습니다.`)) {
      setLoading(true)
      
      try {
        const response = await fetch(`${API_BASE_URL}/api/projects/${projectId}`, {
          method: 'DELETE',
          headers: getAuthHeaders()
        })
        
        if (response.ok) {
          console.log('프로젝트 삭제 성공')
          await loadProjectsFromBackend()
        } else {
          const errorData = await response.json()
          alert(errorData.detail || '프로젝트 삭제에 실패했습니다')
        }
      } catch (error) {
        console.error('프로젝트 삭제 실패:', error)
        alert('프로젝트 삭제 중 오류가 발생했습니다')
      } finally {
        setLoading(false)
      }
    }
  }

  // 프로젝트 복제 (백엔드 API 사용)
  const duplicateProject = async (e, projectId) => {
    e.stopPropagation()
    const project = projects.find(p => p.id === projectId)
    
    setLoading(true)
    
    try {
      const response = await fetch(`${API_BASE_URL}/api/projects/create`, {
        method: 'POST',
        headers: {
          ...getAuthHeaders(),
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          name: `${project.name} (복사본)`,
          description: project.description,
          settings: project.settings || {}
        })
      })
      
      if (response.ok) {
        console.log('프로젝트 복제 성공')
        await loadProjectsFromBackend()
      } else {
        const errorData = await response.json()
        alert(errorData.detail || '프로젝트 복제에 실패했습니다')
      }
    } catch (error) {
      console.error('프로젝트 복제 실패:', error)
      alert('프로젝트 복제 중 오류가 발생했습니다')
    } finally {
      setLoading(false)
    }
  }

  // 프로젝트 내보내기 (백엔드 데이터를 JSON으로)
  const exportProject = async (e, project) => {
    e.stopPropagation()
    
    const dataStr = JSON.stringify(project, null, 2)
    const dataUri = 'data:application/json;charset=utf-8,'+ encodeURIComponent(dataStr)
    const exportFileDefaultName = `${project.name}_${new Date().toISOString().slice(0,10)}.json`
    
    const linkElement = document.createElement('a')
    linkElement.setAttribute('href', dataUri)
    linkElement.setAttribute('download', exportFileDefaultName)
    linkElement.click()
  }

  // 필터링된 프로젝트
  const filteredProjects = projects.filter(project =>
    project.name.toLowerCase().includes(searchTerm.toLowerCase())
  )

  return (
    <div className="projects-page">
      {/* 테스트 페이지 표시 */}
      <div style={{
        background: '#ff6b6b',
        color: 'white',
        padding: '10px',
        textAlign: 'center',
        fontWeight: 'bold'
      }}>
        ⚠️ 테스트 페이지 - 백엔드 API 연동 테스트 중
      </div>

      {/* 사용자 정보 헤더 */}
      <div className="user-header">
        <div className="user-info">
          {user ? (
            <>
              <span className="user-name">
                👤 {user.nickname || user.email || '사용자'}
              </span>
              <button 
                onClick={() => {
                  if (confirm('정말 로그아웃 하시겠습니까?')) {
                    signOut()
                  }
                }}
                className="btn-logout"
                title="로그아웃"
              >
                로그아웃
              </button>
            </>
          ) : (
            <button 
              onClick={handleKakaoLogin} 
              className="btn-login"
              title="카카오 계정으로 로그인"
            >
              <svg 
                width="16" 
                height="16" 
                viewBox="0 0 20 20" 
                fill="none" 
                xmlns="http://www.w3.org/2000/svg"
                style={{ marginRight: '6px' }}
              >
                <path 
                  d="M10 3C5.58172 3 2 5.95885 2 9.56522C2 11.7826 3.29509 13.7132 5.27907 14.7724L4.6939 16.8328C4.60465 17.159 4.96279 17.4295 5.24744 17.2608L7.98488 15.7254C8.63488 15.8565 9.30884 15.9261 10 15.9261C14.4183 15.9261 18 12.9673 18 9.36087C18 5.75449 14.4183 3 10 3Z" 
                  fill="currentColor"
                />
              </svg>
              카카오 로그인
            </button>
          )}
        </div>
      </div>

      {/* 에러 메시지 */}
      {error && (
        <div style={{
          background: '#fee',
          color: '#c00',
          padding: '10px 20px',
          margin: '10px 50px',
          borderRadius: '8px'
        }}>
          ⚠️ {error}
        </div>
      )}

      {/* 로딩 표시 */}
      {loading && (
        <div style={{
          textAlign: 'center',
          padding: '20px',
          color: '#666'
        }}>
          로딩 중...
        </div>
      )}

      {user ? (
        // 로그인한 경우 - 모든 기능 표시
        <div className="projects-header">
          <div className="header-left">
            <h1>내 프로젝트 (백엔드 연동)</h1>
            <span className="project-count">{projects.length}개의 프로젝트</span>
          </div>
          <div className="header-right">
            <input
              type="text"
              placeholder="프로젝트 검색..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="search-input"
            />
            <button 
              onClick={() => setShowNewProjectModal(true)} 
              className="btn-new"
              disabled={loading}
            >
              새 프로젝트
            </button>
          </div>
        </div>
      ) : (
        // 로그인 안 한 경우 - 로그인 유도
        <div className="login-prompt">
          <h1>TERAI에 오신 것을 환영합니다</h1>
          <p>AI 동영상 제작을 시작하려면 로그인이 필요합니다</p>
          <button onClick={handleKakaoLogin} className="btn-login-large">
            <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M10 3C5.58172 3 2 5.95885 2 9.56522C2 11.7826 3.29509 13.7132 5.27907 14.7724L4.6939 16.8328C4.60465 17.159 4.96279 17.4295 5.24744 17.2608L7.98488 15.7254C8.63488 15.8565 9.30884 15.9261 10 15.9261C14.4183 15.9261 18 12.9673 18 9.36087C18 5.75449 14.4183 3 10 3Z" fill="currentColor"/>
            </svg>
            카카오 로그인으로 시작하기
          </button>
        </div>
      )}

      {user && filteredProjects.length === 0 && !loading ? (
        <div className="empty-state">
          <div className="empty-icon">📽️</div>
          <h2>프로젝트가 없습니다</h2>
          <p>새 프로젝트를 만들어 영상 편집을 시작해보세요!</p>
          <button 
            onClick={() => setShowNewProjectModal(true)} 
            className="btn-create-first"
            disabled={loading}
          >
            첫 프로젝트 만들기
          </button>
        </div>
      ) : user && !loading ? (
        <div className="projects-grid">
          {filteredProjects.map(project => (
            <div 
              key={project.id} 
              className="project-card"
              onClick={() => openProject(project.id)}
            >
              <div className="project-thumbnail">
                {project.thumbnail ? (
                  <img src={project.thumbnail} alt={project.name} />
                ) : (
                  <div className="thumbnail-placeholder">
                    <span>🎬</span>
                  </div>
                )}
              </div>
              <div className="project-info">
                <h3>{project.name}</h3>
                <div className="project-meta">
                  <span className="sections-count">
                    📝 {project.settings?.sections?.length || 0}개 섹션
                  </span>
                  <span className="last-modified">
                    🕐 {new Date(project.updated_at || project.created_at).toLocaleDateString()}
                  </span>
                </div>
                {project.description && (
                  <p style={{ fontSize: '12px', color: '#999', marginTop: '5px' }}>
                    {project.description}
                  </p>
                )}
              </div>
              <div className="project-actions">
                <button 
                  onClick={(e) => duplicateProject(e, project.id)}
                  className="btn-action"
                  title="복제"
                >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="18"
                      height="18"
                      viewBox="0 0 12.7 12.7"
                      fill="white"
                    >
                      <path d="m4.6395104.5296835c-.567738 0-1.064328.331462-1.306897.809253-.151891.256082-.06259.586654.197404.731738.254716.142095.576471.05171.719336-.202571.04912-.168465.199665-.281637.390157-.281637h6.0662956c.23423 0 .406177.171946.406177.406177v6.068363c0 .183495-.105741.327565-.264067.381889-.254286.142864-.344667.465137-.202572.719853.145086.26.475656.349295.731739.197404.469608-.244931.79375-.737887.79375-1.299146v-6.068363c0-.802241-.662786-1.46296-1.465027-1.46296zm-2.645317 2.646867c-.802241 0-1.46502698.660202-1.46502698 1.462443v6.0683635c0 .802241.66278598 1.46296 1.46502698 1.46296h6.066296c.802241 0 1.465027-.660719 1.465027-1.46296v-6.0683635c0-.802241-.662786-1.462443-1.465027-1.462443zm0 1.056266h6.066296c.234231 0 .406177.171946.406177.406177v6.0683635c0 .234231-.171946.406694-.406177.406694h-6.066296c-.23423 0-.406177-.172463-.406177-.406694v-6.0683635c0-.234231.171947-.406177.406177-.406177z"/>
                    </svg>
                </button>
                <button 
                  onClick={(e) => exportProject(e, project)}
                  className="btn-action"
                  title="내보내기"
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="18"
                    height="18"
                    viewBox="0 0 24 24"
                    fill="white"
                  >
                    <path d="m8.7 7.7 2.3-2.3v9.6c0 .6.4 1 1 1s1-.4 1-1v-9.6l2.3 2.3c.4.4 1 .4 1.4 0 .4-.4.4-1 0-1.4l-4-4c-.1-.1-.2-.2-.3-.2-.2-.1-.5-.1-.8 0-.1 0-.2.1-.3.2l-4 4c-.4.4-.4 1 0 1.4s1 .4 1.4 0zm12.3 6.3c-.6 0-1 .4-1 1v4c0 .6-.4 1-1 1h-14c-.6 0-1-.4-1-1v-4c0-.6-.4-1-1-1s-1 .4-1 1v4c0 1.7 1.3 3 3 3h14c1.7 0 3-1.3 3-3v-4c0-.6-.4-1-1-1z"/>
                  </svg>
                </button>
                <button 
                  onClick={(e) => deleteProject(e, project.id)}
                  className="btn-action btn-delete"
                  title="삭제"
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="18"
                    height="18"
                    viewBox="0 0 24 24"
                    fill="white"
                  >
                    <path d="M19,7a1,1,0,0,0-1,1V19.191A1.92,1.92,0,0,1,15.99,21H8.01A1.92,1.92,0,0,1,6,19.191V8A1,1,0,0,0,4,8V19.191A3.918,3.918,0,0,0,8.01,23h7.98A3.918,3.918,0,0,0,20,19.191V8A1,1,0,0,0,19,7Z"/>
                    <path d="M20,4H16V2a1,1,0,0,0-1-1H9A1,1,0,0,0,8,2V4H4A1,1,0,0,0,4,6H20a1,1,0,0,0,0-2ZM10,4V3h4V4Z"/>
                    <path d="M11,17V10a1,1,0,0,0-2,0v7a1,1,0,0,0,2,0Z"/>
                    <path d="M15,17V10a1,1,0,0,0-2,0v7a1,1,0,0,0,2,0Z"/>
                  </svg>
                </button>
              </div>
            </div>
          ))}
        </div>
      ) : null}

      {/* 새 프로젝트 모달 */}
      {showNewProjectModal && (
        <div className="modal-overlay" onClick={() => setShowNewProjectModal(false)}>
          <div className="modal-content" onClick={e => e.stopPropagation()}>
            <h2>새 프로젝트 만들기</h2>
            <input
              type="text"
              placeholder="프로젝트 이름을 입력하세요..."
              value={newProjectName}
              onChange={(e) => setNewProjectName(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && !loading && createNewProject()}
              autoFocus
            />
            <input
              type="text"
              placeholder="프로젝트 설명 (선택사항)..."
              value={newProjectDescription}
              onChange={(e) => setNewProjectDescription(e.target.value)}
              style={{ marginTop: '10px' }}
            />
            <div className="modal-buttons">
              <button 
                onClick={createNewProject} 
                className="btn-primary"
                disabled={loading}
              >
                {loading ? '생성 중...' : '만들기'}
              </button>
              <button 
                onClick={() => setShowNewProjectModal(false)} 
                className="btn-secondary"
                disabled={loading}
              >
                취소
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default ProjectsTest